import React from 'react';
import cx from 'classnames';
import DatePicker, { DatePickerProps, registerLocale } from 'react-datepicker';
import da from 'date-fns/locale/da';
import format from 'date-fns/format';
import { type Boundary, detectOverflow, MiddlewareState } from '@floating-ui/react';

import { FlosField, FlosFieldProps } from '../flos-field';
import { FlosInput } from '../input/flos-input';

registerLocale('da', da);

const CustomInput = React.forwardRef<HTMLInputElement, FlosFieldProps>(function CustomInput(
    { label, isValid, iconShape, errorText, wrapperClassName, helpText, isOpen, ...flosInputProps },
    forwardedRef: React.MutableRefObject<HTMLInputElement>
) {
    return (
        <FlosField
            ref={forwardedRef}
            isValid={isValid}
            label={label}
            iconShape={iconShape || 'calendar-02'}
            errorText={errorText}
            wrapperClassName={wrapperClassName}
            disabled={flosInputProps.disabled}
            required={flosInputProps.required}
            id={flosInputProps.id}
            hasDropdown
            isOpen={isOpen}
            helpText={helpText}
            renderInput={({ getInputProps }) => <FlosInput {...getInputProps(flosInputProps)} autoComplete="off" />}
        />
    );
});

export type DatePickerFieldProps = DatePickerProps & {
    onChange?: (date: Date, event: React.ChangeEvent<HTMLInputElement>) => void;
    popperBoundary?: Boundary | null;
} & Omit<FlosFieldProps, 'onValidityChange' | 'maxLength' | 'minLength' | 'editable' | 'custom'>;

/**
 * DateField is a wrapper of `react-datepicker` using styling from ´FlosField´ and ´FlosInput´.
 * You can read more about ´react-datepicker´ here: https://reactdatepicker.com/
 * If you wish to have more control, you can compose them yourself.
 */
export const DateField = React.forwardRef<HTMLInputElement, DatePickerFieldProps>(
    (
        {
            filterDate,
            selected,
            dateFormat = 'dd/MM/yyyy',
            onCalendarClose,
            onCalendarOpen,
            onChangeRaw,
            minDate,
            maxDate,
            inline,
            excludeDates,
            onChange,
            onBlur,
            onFocus,
            errorText,
            helpText,
            isValid,
            value,
            popperModifiers,
            popperBoundary,
            ...rest
        },
        forwardedRef: React.MutableRefObject<HTMLInputElement>
    ) => {
        // ! React Hook "React.useRef" is called conditionally.
        // ? According to eslint(react-hooks/rules-of-hooks): React Hooks must be called in the exact same order in every component render.
        // Create a fallback ref if user didn't pass any ref when they use DateField component.
        const fallbackRef = React.useRef<React.MutableRefObject<HTMLInputElement>>();
        const DateFieldRef = forwardedRef || fallbackRef;
        const [dateInString, setDateInString] = React.useState(value);
        const [selectedDate, setSelectedDate] = React.useState(selected);
        const hintText = isValid === false ? errorText : helpText;
        const sanitizedDateFormat = `${dateFormat}`.replace(/[A-LN-Z]+/g, (match) => match.toLowerCase());

        const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

        const openHandler = () => {
            setIsCalendarOpen(true);
            onCalendarOpen && onCalendarOpen();
        };

        const closeHandler = () => {
            setIsCalendarOpen(false);
            onCalendarClose && onCalendarClose();
        };

        const handleChange = React.useCallback(
            (date: Date) => {
                setSelectedDate(date);
                const formattedDate = format(date, sanitizedDateFormat, { locale: da });
                setDateInString(formattedDate);
                if (!onChange) return;
                const newEvent: React.ChangeEvent<HTMLInputElement> = Object.assign({ target: Object.assign({}, DateFieldRef.current, { value: formattedDate }) });
                onChange(date, newEvent);
            },
            [DateFieldRef, onChange, sanitizedDateFormat]
        );
        React.useEffect(() => {
            if (!value) {
                // it seems the intention is to be able to use either value or selected
                const dateStrToUse = selected ? format(selected, sanitizedDateFormat, { locale: da }) : '';
                setDateInString(dateStrToUse);
            } else {
                const timestamp = Date.parse(value);
                const newDate = new Date(timestamp);
                if (newDate instanceof Date && !isNaN(timestamp)) {
                    handleChange(newDate);
                }
            }
        }, [selected, value, sanitizedDateFormat, handleChange]);

        return (
            <div className={cx('flos-date-field', { 'flos-date-field--with-helptext': !!hintText })}>
                <DatePicker
                    ref={(datePickerInput) => datePickerInput && (DateFieldRef.current = datePickerInput['input'] as HTMLInputElement)}
                    id={rest.id}
                    inline={inline}
                    required={rest.required}
                    placeholderText={' '}
                    selected={selectedDate}
                    onChange={handleChange}
                    onChangeRaw={onChangeRaw}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    showPopperArrow={false}
                    popperModifiers={[
                        {
                            name: 'flos',
                            fn: (state) => {
                                const placementTop = state.placement.includes('top');
                                const placementBottom = state.placement.includes('bottom');
                                const aboveClass = 'flos-input-dialog-above';
                                const belowClass = 'flos-input-dialog-below';
                                if (placementBottom) {
                                    state.y = state.y - 10;
                                    DateFieldRef.current.classList.add(belowClass);
                                } else {
                                    DateFieldRef.current.classList.remove(belowClass);
                                }
                                if (placementTop) {
                                    state.y = state.y + 10;
                                    DateFieldRef.current.classList.add(aboveClass);
                                } else {
                                    DateFieldRef.current.classList.remove(aboveClass);
                                }
                                return state;
                            },
                        },
                        {
                            name: 'detechOverflow',
                            fn: async (state: MiddlewareState) => {
                                const overflow = await detectOverflow(state, { boundary: popperBoundary as Boundary });
                                const overflowTop = overflow.top > 0 && state.placement.includes('top');
                                const overflowBottom = overflow.bottom > 0 && state.placement.includes('bottom');
                                const overflowLeft = overflow.left > 0 && state.placement.includes('left');
                                const overflowRight = overflow.right > 0 && state.placement.includes('right');
                                if (overflowTop) state.y += overflow.top;
                                if (overflowBottom) state.y -= overflow.bottom;
                                if (overflowLeft) state.x += overflow.left;
                                if (overflowRight) state.x -= overflow.right;

                                return state;
                            },
                        },
                        ...(popperModifiers || []),
                    ]}
                    locale={'da'}
                    filterDate={filterDate}
                    dateFormat={sanitizedDateFormat}
                    onCalendarOpen={openHandler}
                    onCalendarClose={closeHandler}
                    minDate={minDate}
                    maxDate={maxDate}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    peekNextMonth
                    disabled={rest.disabled}
                    excludeDates={excludeDates}
                    value={dateInString}
                    customInput={<CustomInput helpText={helpText} errorText={errorText} isValid={isValid} isOpen={isCalendarOpen} {...rest} ref={DateFieldRef} />}
                />
            </div>
        );
    }
);

DateField.displayName = 'DateField';
